import { gql } from "lib/apolloClient";
import { unitPlanFragment } from "AppComponents/Post/modules/PostFragments";
import {
  toolResponseFragment,
  genericTagFragment,
  courseFragment,
  studentsAlertFragment,
  taggedPracticesFragment,
} from "modules/CommonFragments";
import {
  elementRemarkFragment,
  resolvedFieldValueFragment,
} from "ClassRoom/modules/ClassRoomFragments";

const pypElementNodeFragment = {
  focusArea: gql`
    fragment focusAreaItem on FocusArea {
      id
      label
    }
  `,
  subjectGroupBasicDetails: gql`
    fragment subjectGroupBasicDetailsItem on SubjectGroup {
      id
      name
      constants
    }
  `,
  genericTag: gql`
    fragment basicGenericTagItem on GenericTag {
      id
      type
      label
    }
  `,
  standardSet: gql`
    fragment standardSetItem on BenchmarkStandardSet {
      id
      title
      description
      code
    }
  `,
};

const pypElementFragment = {
  theme: gql`
    fragment themeItem on PlannerTheme {
      id
      label
      descriptor
      metadata
      associatedChildren {
        id
        label
      }
      focusAreas {
        ...focusAreaItem
      }
      icon
    }
    ${pypElementNodeFragment.focusArea}
  `,
  lp: gql`
    fragment lpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
    }
  `,
  concept: gql`
    fragment conceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
    }
  `,
  atl: gql`
    fragment atlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      grades {
        id
        name
      }
    }
  `,
  benchmark: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parent
      benchmarkQuestionCount {
        count
        color
        url
        yearSplit
      }
      levels {
        id
        value
        count
      }
      subject
      levelId
      displaySequence
      depth
      type
      code
      grades {
        id
        name
      }
      isLeaf
      resolvedSubject {
        id
        isArchived
      }
    }
  `,
  relatedConcept: gql`
    fragment relatedConceptItem on PlannerRelatedConcept {
      id
      label
      elementId
      elementType
      parent
      children
    }
  `,
  action: gql`
    fragment actionItem on Action {
      id
      label
      subText
      children
      parent
      levels {
        id
        value
      }
      levelId
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      subText
      depth
      children
      parent
      subType
      isLeaf
      code
      metadata
      updatedAt
      associatedParents {
        id
        ... on Grade {
          id
          name
        }
        ... on Subject {
          id
          name
          isArchived
        }
        ... on SubjectGroup {
          ...subjectGroupBasicDetailsItem
        }
        ... on PlannerElementNode {
          id
          label
          type
          metadata
        }
      }
    }
    ${pypElementNodeFragment.subjectGroupBasicDetails}
  `,
  attachmentGroup: gql`
    fragment attachmentGroupItem on AttachmentGroup {
      id
      metadata
      attachments {
        ... on Attachment {
          id
          name
          type
          mimeType
          url
          signedUrl
          thumbUrl
          title
        }
      }
      studentTemplate {
        id
      }
    }
  `,
  attachment: gql`
    fragment attachmentItem on Attachment {
      id
      name
      type
      mimeType
      url
      signedUrl
      thumbUrl
      title
      metadata
      streamUrl
      parentType
      isRead
      similarityReport {
        status
        submissionId
        errorCode
        overallMatchPercentage
      }
      createdBy {
        id
        type
      }
      lockingState {
        state
        scheduledAt
      }
      questionAttachment {
        ... on QuestionSubmission {
          id
          questionScore
          score {
            id
            value
          }
        }
        ... on Question {
          id
          questionScore
        }
      }
    }
  `,
  fieldScore: gql`
    fragment fieldScoreItem on ResolvedFieldScore {
      id
      type
      isUsingScore
      maxScore
      category {
        id
        label
        colorCode
        set {
          id
          associatedEntities {
            id
            ... on Course {
              id
              title
            }
            ... on Subject {
              id
              name
            }
          }
        }
      }
    }
  `,
  subjectGroupWithGlobalSubjectGroup: gql`
    fragment subjectGroupWithGlobalSubjectGroupItem on SubjectGroup {
      id
      name
      constants
      globalSubjectGroup {
        id
        name
        constants
      }
    }
  `,
};

const subjectGroupFragment = {
  subjectBenchmarkDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      icon
      grades {
        id
        name
      }
      isArchived
      benchmarkRootNode {
        ...benchmarkItem
      }
      isArchived
      globalSubject
      framework
      snsChildren
      standardSet {
        ...standardSetItem
      }
      subjectLevelLabels {
        cuLevelName
        strandLevelName
        loLevelName
      }
      type
      tags {
        id
        key
        value
      }
      variants {
        level {
          ...basicGenericTagItem
        }
        responseLanguages {
          ...basicGenericTagItem
        }
      }
      flatVariants(filters: { archivalState: ALL }) {
        isArchived
        level {
          ...basicGenericTagItem
        }
        responseLanguage {
          ...basicGenericTagItem
        }
      }
    }
    ${pypElementFragment.benchmark}
    ${pypElementNodeFragment.genericTag}
    ${pypElementNodeFragment.standardSet}
  `,
};

const resolvedFieldValueItemFragment = gql`
  fragment resolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...plannerElementNodeItem
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        associatedParents {
          id
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
  ${pypElementFragment.plannerElementNode}
  ${pypElementFragment.benchmark}
  ${pypElementFragment.concept}
  ${pypElementFragment.action}
  ${pypElementFragment.lp}
  ${pypElementFragment.theme}
  ${pypElementFragment.atl}
  ${pypElementFragment.relatedConcept}
  ${pypElementFragment.fieldScore}
  ${pypElementFragment.attachment}
  ${pypElementFragment.attachmentGroup}
  ${pypElementFragment.subjectGroupWithGlobalSubjectGroup}
  ${subjectGroupFragment.subjectBenchmarkDetails}
`;

const resolvedFieldValueFragmentV2 = {
  resolvedMinimalTree: gql`
    fragment unitPlanResolvedMinimalTreeItem on ResolvedFieldValue {
      ...resolvedFieldValueItem
    }
    ${resolvedFieldValueItemFragment}
  `,
};

const plannerFieldFragment = {
  unitPlanAllFields: gql`
    fragment unitPlanAllFields on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
    }
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
};

export const academicCriteriaValueFragment = {
  academicCriteriaValue: gql`
    fragment academicCriteriaValueItem on AcademicCriteriaValue {
      id
      label
      abbreviation
      displaySequence
    }
  `,
};

export const assignedStudentFragment = {
  assignedStudent: gql`
    fragment assignedStudentItem on StudentAssignment {
      id
      student {
        id
      }
      toolResponses: toolResponsesV2(
        filters: { userFilter: ALL, isFetchDeletedAiRatings: true }
      ) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
      ibPYPElementRatings(filters: { isFetchDeletedAiRatings: true }) {
        id
        value
        isDeleted
        creatorType
        ibPYPElementId
        ibPYPElementType
        evaluationJustification {
          id
          message
        }
        academicCriteriaValue {
          id
          label
          abbreviation
        }
        academicCriteriaSet {
          id
        }
      }
      elementRemarks(filters: { isFetchDeletedAiRatings: true }) {
        ...elementRemarkItem
      }
      isEvaluated
      isSharedWithStudent
      isEvaluationApplicable
      status
      isSubmitted
      evaluatedAt
      submission {
        id
        submittedAt
        statusV2
        systemStatus
        overrideStatusUpdatedAt
        overrideStatusUpdatedBy {
          id
          type
        }
      }
      lockingState {
        state
      }
    }

    ${toolResponseFragment.toolResponse}
    ${elementRemarkFragment.elementRemark}
  `,
};

export const assignmentFragment = {
  assignmentProgressSummary: gql`
    fragment assignmentProgressSummaryItem on Assignment {
      id
      contentType
      createdAt
      isPrivate
      state {
        state
      }
      isTeacherEvaluationEnabled
      isStudentEvaluationEnabled
      isStudentSubmissionEnabled
      isExcludedFromFinalGradeCalculation
      course {
        id
      }
      deadline
      content {
        id
        ... on Assessment {
          id
          assessmentType {
            id
            value
          }
          title {
            id
            value
          }
          assessmentTools(
            filters: { assessmentToolType: $assessmentToolTypes }
          ) {
            edges {
              node {
                id
                assessmentToolType
                assessmentToolItem {
                  id
                  ... on AcademicCriteriaSet {
                    id
                    academicCriteriaValues {
                      id
                      label
                      abbreviation
                      scoreValues {
                        id
                        genericTag {
                          id
                          label
                        }
                        minValue
                        maxValue
                      }
                    }
                    criteriaType
                  }
                  ... on MypObjectiveRubric {
                    id
                    label
                    criteria {
                      id
                      highestMark
                    }
                  }

                  ... on ScoreAssessmentTool {
                    id
                    scoreToolData {
                      id
                      maxScore
                    }
                  }
                }
                isTeacherEvaluationEnable
                isStudentEvaluationEnable
              }
            }
          }
          templateId
          unitPlan {
            id
            allFields {
              ...unitPlanAllFields
            }
            ...unitPlanItem
          }
          fields(
            uids: [
              "measureAssessing"
              "objectivesMYP"
              "interdisciplinaryCriteriaMYP"
              "subjectLevel"
              "atls"
              "benchmarks"
              "concepts"
              "lp"
              "learningStandardUBD"
              "atlsUBD"
              "learningStandardMYP"
              "atlsMYP"
              "subjectStandardDP"
              "atlDP"
              "learnerProfileDP"
              "syllabusContentDP"
            ]
          ) {
            id
            uid
            value
            resolvedMinimalTree {
              ...resolvedMinimalTreeItem
            }
          }
          category {
            id
            label
            colorCode
          }
          academicTerm {
            id
          }
          weightage
          isLearningGoalsEvaluationEnabled
        }
      }
      assignedStudents {
        ...assignedStudentItem
      }
      counts {
        totalDraftSubmissions
      }
    }
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${unitPlanFragment.unitPlanDetails}
    ${plannerFieldFragment.unitPlanAllFields}
    ${assignedStudentFragment.assignedStudent}
  `,
};

export const categoryWiseAssessmentsFragment = {
  assignmentsFragment: gql`
    fragment categoryWiseAssessmentItem on AcademicCriteriaValue {
      id
      assignments(filters: $assignmentFilters) {
        ...assignmentProgressSummaryItem
      }
    }
    ${assignmentFragment.assignmentProgressSummary}
  `,
};

export const organizationAssignmentsFragment = {
  organizationAssignmentProgressSummary: gql`
    fragment organizationAssignmentItem on Organization {
      id
      assignments(
        first: $first
        after: $after
        filters: {
          courseIds: $courseIds
          subjectIds: $subjectIds
          states: $states
          categoryIds: $categoryIds
          searchText: $searchText
          academicTermIds: $academicTermIds
          assessmentTypes: $assessmentTypes
          excludedAssessmentTypes: $excludedAssessmentTypes
          groupTypes: $groupTypes
          isTeacherEvaluationEnabled: $isTeacherEvaluationEnabled
          isAssessmentScoringEnabled: $isAssessmentScoringEnabled
          contentTypes: $contentTypes
          assessmentToolTypes: $assessmentToolTypes
          includedStudentIds: $includedStudentIds
          primaryGradeId: $primaryGradeId
          isExcludedFromFinalGradeCalculation: $isExcludedFromFinalGradeCalculation
        }
      ) {
        edges {
          node {
            ...assignmentProgressSummaryItem
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
    ${assignmentFragment.assignmentProgressSummary}
  `,
};

export const basicStaffDetailsFragment = gql`
  fragment basicStaffDetails on Staff {
    id
    firstName
    middleName
    lastName
    preferredName
    prefix
    suffix
    pronouns
    profileImage
  }
`;

export const studentFragment = {
  basicStudentDetails: gql`
    fragment studentItem on Student {
      id
      firstName
      middleName
      lastName
      pronouns
      preferredName
      prefix
      suffix
      profileImage
      email
      type
      isDevUser
      isArchived
      isArchivedV2(filters: $userArchivedFilters)
      sourceId
      type
    }
  `,
};

export const courseUserMapFragment = {
  courseUserMap: gql`
    fragment courseItem on Course {
      id
      userMap(filters: $filters) {
        edges {
          node {
            id
            user {
              id
              ... on Student {
                ...studentItem
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
            isRemovedFromClass
            tags {
              id
              ...basicGenericTagItem
            }
          }
        }
      }
    }
    ${studentsAlertFragment}
    ${studentFragment.basicStudentDetails}
    ${genericTagFragment.genericTag}
  `,
};

export const gradebookAssignmentFragment = {
  gradebookAssignment: gql`
    fragment assignmentProgressSummaryItem on Assignment {
      id
      contentType
      createdAt
      isPrivate
      state {
        state
      }
      isTeacherEvaluationEnabled
      isStudentEvaluationEnabled
      isStudentSubmissionEnabled
      isLockSubmissionEnabled
      isExcludedFromFinalGradeCalculation
      course {
        id
        title
        isMappedToMe
        aclControlParameters(filters: $aclControlParametersFilters) {
          id
          permission
          value
        }
        learningCourse {
          id
        }
        primaryGrade {
          id
        }
      }
      deadline
      publishedAt
      closeSubmissionDate
      isBackdateAssignment
      isTimedTestEnabled
      isFixedStartTime
      metadata
      summaryCounts {
        lockedSubmissions
        unlockedSubmissions
        assigned
        turnedIn
        evaluated
        evaluationShared
        evaluationApplicable
      }
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
      content {
        id
        ... on Assessment {
          id
          assessmentType {
            id
            value
          }
          taskType {
            id
            type
            label
            color
            displaySequence
            metaData
          }
          title {
            id
            value
          }
          academicTerm {
            id
            lockingStatus
          }
          assessmentTools(filters: $assessmentToolsFilters) {
            edges {
              node {
                id
                assessmentToolType
                assessmentToolItem {
                  id
                  ... on AcademicCriteriaSet {
                    id
                    academicCriteriaValues {
                      id
                      label
                      abbreviation
                      scoreValues {
                        id
                        genericTag {
                          id
                          label
                        }
                        minValue
                        maxValue
                      }
                    }
                    criteriaType
                  }
                  ... on MypObjectiveRubric {
                    id
                    label
                    criteria {
                      id
                      highestMark
                    }
                  }

                  ... on ScoreAssessmentTool {
                    id
                    scoreToolData {
                      id
                      maxScore
                    }
                  }

                  ... on StandardRubric {
                    id
                    associateScoreTool {
                      id
                      scoreToolData {
                        id
                        maxScore
                      }
                    }
                    standardRubricCriterias {
                      id
                      descriptors: standardRubricDescriptors {
                        id
                        score
                        gradeScaleValue {
                          id
                        }
                      }
                    }

                    gradeScale {
                      id
                      gradingType
                    }
                  }

                  ... on GradeScaleEntityDetail {
                    id
                    gradeScale {
                      id
                    }
                    gradeScaleValues {
                      id
                      label
                      abbreviation
                      displaySequence
                      gradeScaleValueScores {
                        id
                        minValue
                        maxValue
                        gradeValue
                      }
                    }
                  }
                }
                isTeacherEvaluationEnable
                isStudentEvaluationEnable
              }
            }
          }
          templateId
          fields(
            uids: [
              "measureAssessing"
              "objectivesMYP"
              "interdisciplinaryCriteriaMYP"
              "subjectLevel"
              "atls"
              "benchmarks"
              "concepts"
              "lp"
              "learningStandardUBD"
              "atlsUBD"
              "learningStandardMYP"
              "atlsMYP"
              "subjectStandardDP"
              "atlDP"
              "learnerProfileDP"
              "syllabusContentDP"
              "lpMYP"
              "aiEvaluation"
              "subjects"
            ]
          ) {
            id
            uid
            value
            resolvedMinimalTree {
              ...resolvedMinimalTreeItem
            }
          }
          category {
            id
            label
            colorCode
          }
          globalCategories {
            id
            name
          }
          academicTerm {
            id
          }
          weightage
          isLearningGoalsEvaluationEnabled
          assignments {
            totalCount
            userAssignmentsCount
          }
          isIncludedInCourseFlow
          lockingInfo {
            isLocked
          }
          gradingConfigurations {
            id
            settings {
              id
              uid
              value
            }
          }
        }
      }
      assignedStudents {
        ...assignedStudentItem
      }
      counts {
        totalDraftSubmissions
      }
    }
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${assignedStudentFragment.assignedStudent}
    ${taggedPracticesFragment.taggedPractices}
  `,
};

export const gradebookAssignmentsFragment = {
  gradebookAssignments: gql`
    fragment organizationAssignmentItem on Organization {
      id
      assignments(
        first: $first
        after: $after
        filters: $assignmentFeedFilters
        customOrderBy: $customOrderBy
      ) {
        edges {
          node {
            ...assignmentProgressSummaryItem
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
    ${gradebookAssignmentFragment.gradebookAssignment}
  `,
};

export const subjectFragment = {
  subjectDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      icon
      type
      grades {
        id
        name
      }
      courses {
        edges {
          node {
            ...courseGradeItem
          }
        }
        totalCount
      }
      isArchived
      assignmentCount(
        filters: $assignmentCountFilters
        curriculumProgramType: $curriculumProgramType
      )
    }
    ${courseFragment.courserGradesDetails}
  `,
};

export const organizationFragment = {
  organizationSubjects: gql`
    fragment organizationItem on Organization {
      id
      subjects(filters: $filters) {
        ...subjectItem
      }
    }
    ${subjectFragment.subjectDetails}
  `,
};

export const categoriesItemFragment = {
  categoryItem: gql`
    fragment categoryItem on PointAssessmentSetCategory {
      id
      label
      colorCode
      percentageWeight
      hlWeight
      slWeight
      values {
        id
        value
        minValue
        maxValue
        genericTag {
          id
          label
        }
        valueType
        weightageType
      }
      gradeBoundary {
        id
        categories {
          id
          label
          displaySequence
          values {
            id
            value
            minValue
            maxValue
            genericTag {
              id
              label
            }
          }
        }
      }
      gradeScaleType
      displaySequence
      overridenCategoryScoresCount
      assignments(filters: $assignmentFeedFilters) @skip(if: $skipAssignments) {
        totalCount
      }
    }
  `,
};

export const studentRatingsFragment = {
  studentRatings: gql`
    fragment StudentRatingsItem on Student {
      id
      ibPYPElementRatings(
        dataSet: ASSESSMENT_CATEGORY_AND_TERM_FINAL_SCORE
        filtersForGeneric: $filtersForGeneric
      ) {
        id
        academicCriteriaValue {
          id
          label
          abbreviation
          displaySequence
        }
        academicCriteriaSet {
          id
          criteriaType
          subType
        }
        ibPYPElementId
        ibPYPElementType
        isComputedRating
        isRemoved
        category {
          id
        }
        globalCategory {
          id
        }
        parent {
          id
          ... on StudentAssignment {
            id
          }
        }
        parentType
        academicTerm {
          id
        }
        value
      }
    }
  `,
};

export const academicCriteriaSetFragment = {
  academicCriteriaSet: gql`
    fragment academicCriteriaSetItem on AcademicCriteriaSet {
      id
      label
      criteriaType
      subType
    }
  `,
};
